import { useTranslate } from "../../../../customHooks";
import { usePermissions } from '../../../../customHooks';
import { useCallback, useMemo } from "react";
import { toast } from "../../../../components";
import { FormLayout, RadioGroup, SubmitButton, Form, Checkbox } from "@panwds/react-form";
import { isEmpty } from 'lodash';
import { useUpdateFirewallMutation } from "../../../../redux/services/firewalls-service";
import { extractFieldsWithConfig } from "../../../../utils/utils";
import { useForm, FormProvider } from 'react-hook-form';
import { Banner, Button, Link, LoadingPanel, ModalBody, ModalCloseButton, ModalFooter, ModalHeader, RenderModalProps, Subtitle, useModal } from "@panwds/react-ui";
import { FormattedMessage } from "react-intl";
import fwStyles from "../../FirewallEdit.module.scss";
import TabPageHeader from "./TabPageHeader";
import { FirewallUpdateBanner } from "../../FirewallUpdateBanner";
import { isFirewallUpdating } from "../../firewallsUtil";

interface FwTierUpdateProps {
    update: () => Promise<void>;
    cancel: () => void;
}

const FwTierUpdateConfirmationDialogModal = ({
    closeModal,
    isOpen,
    update,
    cancel,
}: RenderModalProps<FwTierUpdateProps>) => {
    const translate = useTranslate();
    return <Form
        dataMetrics="cloudngfw-firewall-tier-update-confirm-modal"
        modalProps={{ onClose: closeModal, isOpen }}
        onSubmit={update}
    >
        <ModalHeader title={translate(`generic.notification`)} />
        <ModalBody>
            <div className='formLayout'>
                <>
                    <Subtitle size="lg" as={"h3"}>{translate(`resources.firewallsV2.tier.confirmDialogTitle`)}</Subtitle>
                    <Subtitle size="sm" as={"p"}>{translate(`resources.firewallsV2.tier.confirmDialogMessage`)}</Subtitle>
                    <Checkbox
                        label={translate('resources.firewallsV2.tier.confirmCheckboxText')}
                        name="EnableInvite"
                        showLabel
                        register={{
                            required: translate('validation.required')
                        }}
                    />
                </>
            </div>
        </ModalBody>
        <ModalFooter>
            <ModalCloseButton onClick={() => { closeModal(); cancel(); }}>{translate('generic.cancel')}</ModalCloseButton>
            <SubmitButton>{translate(`generic.confirm`)}</SubmitButton>
        </ModalFooter>
    </Form>
};

const FirewallTier = (props: { canEdit: boolean, firewallData: Record<string, any>, isLoading: boolean, refreshFirewallData: () => void }) => {
    const translate = useTranslate();
    const { permissions } = usePermissions();
    const region = new URLSearchParams(location.search).get('region');
    const [updateFirewall, { isLoading: isUpdating }] = useUpdateFirewallMutation();
    const firewallUpdating = isFirewallUpdating(props?.firewallData?.Status?.FirewallStatus);

    const {
        openModal, closeModal
    } = useModal(FwTierUpdateConfirmationDialogModal);

    const tierOptions = useCallback(() => {
        return [{
            value: 'base',
            children: <span>{translate(`resources.firewallsV2.tier.base`)}</span>,
            disabled: props?.firewallData?.Firewall.Tier === "standard" || props?.firewallData?.Firewall.Tier === "premium"
        }, {
            value: 'standard',
            children: <span>{translate(`resources.firewallsV2.tier.standard`)}</span>,
            disabled: props?.firewallData?.Firewall.Tier === "premium"
        }, {
            value: 'premium',
            children: <span>{translate(`resources.firewallsV2.tier.premium`)}</span>
        },]
    }, [props?.firewallData])

    const config = {
        Firewall: ['FirewallId', 'Tier', 'UpdateToken', 'DeploymentUpdateToken'],
    };

    // Memoized function to transform the default form values
    const transformFormDefaultValue = useMemo(() => {
        if (isEmpty(props?.firewallData)) {
            return undefined;
        }
        // Extract fields from firewall describe response based on the config.
        // Extracts fields needed for firewall general info form.
        const newStateWithConfig = extractFieldsWithConfig(props?.firewallData, config);
        return {
            Firewall: {
                ...newStateWithConfig.Firewall,
            }
        }
    }, [props?.firewallData]);

    /**
  * Transforms the form data for firewall update submission.
  * This function processes the form data, applies necessary transformations,
  * and prepares the data structure required for the firewall general info update.
  */
    const transformFormSubmitValue = useCallback((formData: Record<string, any>) => {
        if (isEmpty(formData)) {
            return;
        }
        const transformedData = {
            ...formData.Firewall,
            Region: region
        }

        // Remove FirewallName but keep FirewallId
        delete transformedData['FirewallName'];

        return transformedData;
    }, [region]);

    const formMethods = useForm({ defaultValues: transformFormDefaultValue });
    const {
        formState: { dirtyFields }, reset, watch,
    } = formMethods

    const currentTier = watch("Firewall.Tier");

    const updateFirewallTier = useCallback(
        async (data) => {
            try {
                const payload = transformFormSubmitValue(data);

                const result = await updateFirewall({
                    payload: payload
                }).unwrap();

                if (result) {
                    // Close the modal first
                    closeModal();
                    // Then refresh the data
                    await props.refreshFirewallData();
                }
            }
            catch (error: any) {
                toast.error(`${error?.code || 'Error'}: ${error?.error || error?.message || 'Failed to update firewall tier'}`, { toastId: "update-firewall-tier-error" });
                closeModal();
                reset();
            }
        }, [updateFirewall, transformFormSubmitValue, props.refreshFirewallData, reset, closeModal, translate]
    );

    const onSubmit = useCallback(
        (data) => {
            openModal({
                update: async () => {
                    await updateFirewallTier(data);
                },
                cancel: () => handleCancel(),
            })
        }, [updateFirewallTier, openModal]
    );

    const handleCancel = () => {
        reset(transformFormDefaultValue);
    };

    return (
        <div className="tw-relative">
            {(props?.isLoading || isUpdating) &&
                <div className="tw-absolute tw-inset-0 tw-right-0 tw-flex tw-justify-center tw-items-center tw-backdrop-blur-sm tw-z-50" style={{ backgroundColor: 'rgba(255, 255, 255, 0.7)' }}>
                    <div className="tw-w-6 tw-h-6">
                        <LoadingPanel />
                    </div>
                </div>
            }
            {!props?.canEdit &&
                <FirewallUpdateBanner content={translate('resources.firewallsV2.cantEditFirewall')} />
            }
            {firewallUpdating &&
                <FirewallUpdateBanner refresh={props.refreshFirewallData} />
            }
            <TabPageHeader title={translate(`resources.firewallsV2.tier.title`)} addClassName="tw-pb-4" />
            <div className={!props?.canEdit || firewallUpdating || !permissions?.UpdateFirewall ? fwStyles.fwReadOnly : ""}>
                <FormProvider {...formMethods}>
                    <form onSubmit={formMethods.handleSubmit(onSubmit)}>
                        <FormLayout>
                            <RadioGroup horizontal
                                label={""}
                                name="Firewall.Tier"
                                dataMetrics={"cloudngfw-firewall-edit-tier"}
                                items={tierOptions()}
                            />
                            <Banner
                                type="inline"
                                showIcon>
                                <span>
                                    {currentTier === 'base' ? (
                                        <FormattedMessage
                                            id="resources.firewallsV2.tier.baseInfo"
                                            values={{
                                                smallerPolicySetsLink: (
                                                    <Link external href="https://docs.paloaltonetworks.com/cloud-ngfw-aws/reference/cloud-ngfw-for-aws-limits-and-quotas?otp=concept-gcm_1v5_lbc#concept-gcm_1v5_lbc">
                                                        smaller policy sets
                                                    </Link>
                                                )
                                            }}
                                        />
                                    ) : currentTier === 'standard' ? (
                                        <FormattedMessage
                                            id="resources.firewallsV2.tier.standardInfo"
                                            values={{
                                                largerPolicySetsLink: (
                                                    <Link external href="https://docs.paloaltonetworks.com/cloud-ngfw-aws/reference/cloud-ngfw-for-aws-limits-and-quotas?otp=concept-rz5_1v5_lbc#concept-rz5_1v5_lbc">
                                                        larger policy sets
                                                    </Link>
                                                ),
                                                noAdditionalPriceLink: (
                                                    <Link external href="https://docs.paloaltonetworks.com/cloud-ngfw-aws/reference/pricing">
                                                        no additional price
                                                    </Link>
                                                )
                                            }}
                                        />
                                    ) : (
                                        <FormattedMessage
                                            id="resources.firewallsV2.tier.premiumInfo"
                                            values={{
                                                muchLargerPolicySetsLink: (
                                                    <Link external href="https://docs.paloaltonetworks.com/cloud-ngfw-aws/reference/cloud-ngfw-for-aws-limits-and-quotas?otp=concept-rz5_1v5_lbc#concept-rz5_1v5_lbc">
                                                        much larger policy sets
                                                    </Link>
                                                ),
                                                higherPriceLink: (
                                                    <Link external href="https://docs.paloaltonetworks.com/cloud-ngfw-aws/reference/pricing">
                                                        higher price
                                                    </Link>
                                                )
                                            }}
                                        />
                                    )}
                                </span>
                            </Banner>
                            <div className={fwStyles.formFooterDivider} />
                            <div className="tw-flex tw-justify-end">
                                <Button addClassName="tw-mr-2" onClick={handleCancel} disabled={isEmpty(dirtyFields) || isUpdating}>
                                    {translate(`generic.cancel`)}
                                </Button>
                                <SubmitButton disabled={isEmpty(dirtyFields) || isUpdating}>
                                    {isUpdating ? translate('generic.updating') : translate(`generic.save`)}
                                </SubmitButton>
                            </div>
                        </FormLayout>
                    </form>
                </FormProvider>
            </div>
        </div>
    );
};

export default FirewallTier;
